<template>
  <div class="ced">
    <div v-if="!campaignId" class="ced-empty">Campanha não informada.</div>

    <template v-else>
      <EditorHeader :header="header" :campaign-id="campaignId" @back="$router.go(-1)" />

      <div class="ced-layout">
        <nav class="ced-nav" aria-label="Seções da campanha">
          <button
            v-for="(tab, index) in tabs"
            :key="tab.key"
            type="button"
            class="ced-nav__item"
            :class="{ 'ced-nav__item--active': index === activeIndex }"
            :aria-current="index === activeIndex ? 'page' : null"
            @click="onTabChange(index)"
          >
            <i :class="['fal', tab.icon, 'ced-nav__icon']"></i>
            <span class="ced-nav__label">{{ tab.label }}</span>
            <span v-if="dirtyTabs[tab.key]" class="ced-nav__dirty" title="Alterações não salvas"></span>
          </button>
        </nav>

        <main class="ced-content">
          <div class="ced-content__head">
            <h2 class="ced-content__title">{{ activeTab.label }}</h2>
            <p class="ced-content__description">{{ activeTab.description }}</p>
          </div>
          <!-- Cada seção é carregada (import dinâmico + GET próprio) só na 1ª ativação e
               permanece montada (v-show) para não perder edições ao alternar. -->
          <component
            v-for="tab in mountedTabs"
            v-show="tab.key === activeTab.key"
            :key="tab.key"
            :is="tab.component"
            :campaign-id="campaignId"
            :title="tab.label"
            :version-bump="versionBump"
            @dirty-change="setDirty(tab.key, $event)"
            @saved="onSaved"
            @header-changed="onHeaderChanged"
          />
        </main>
      </div>
    </template>
  </div>
</template>

<script>
import CampaignEditorService from "@/scripts/services/campaignEditor.service";
import EditorHeader from "./EditorHeader.vue";
import "./editor.css";

const service = new CampaignEditorService();

// Ordem e chaves das abas (a chave vai na URL: ?aba=...).
const TABS = Object.freeze([
  { key: "geral", label: "Geral", icon: "fa-info-circle", description: "Nome, vigência, tipo, valores e quem pode participar.", component: () => import("./tabs/GeneralTab.vue") },
  { key: "divulgacao", label: "Divulgação", icon: "fa-share-alt", description: "Como a campanha aparece ao ser compartilhada e as ferramentas de medição.", component: () => import("./tabs/SharingTab.vue") },
  { key: "checkout", label: "Doação e checkout", icon: "fa-shopping-cart", description: "Regras da doação e os dados pedidos ao doador.", component: () => import("./tabs/CheckoutTab.vue") },
  { key: "pagamentos", label: "Pagamentos e taxas", icon: "fa-credit-card", description: "Meios de pagamento, gateways, parcelamento, recorrência e taxas.", component: () => import("./tabs/PaymentsTab.vue") },
  { key: "termos", label: "Termos e LGPD", icon: "fa-file-signature", description: "Termo de anuência exibido no checkout e consentimento LGPD.", component: () => import("./tabs/TermsTab.vue") },
  { key: "produtos", label: "Produtos e sorteio", icon: "fa-gift", description: "Produtos e brindes da campanha e a distribuição dos números da sorte.", component: () => import("./tabs/ProductsTab.vue") },
  { key: "projetos", label: "Projetos", icon: "fa-folder-open", description: "Projetos que recebem doações nesta campanha e seus apoiadores.", component: () => import("./tabs/ProjectsTab.vue") },
  { key: "integracoes", label: "Integrações", icon: "fa-plug", description: "Salesforce, APIs de funcionários, SSO e simulador de IR.", component: () => import("./tabs/IntegrationsTab.vue") },
].map((tab) => Object.freeze(tab)));

const LEAVE_CONFIRMATION = "Há alterações não salvas. Deseja sair mesmo assim e descartá-las?";

export default {
  name: "CampaignEditor",
  components: { EditorHeader },
  props: ["pageOptions"],
  data() {
    return {
      tabs: TABS,
      activeIndex: 0,
      mountedKeys: [],
      dirtyTabs: {},
      header: null,
      versionBump: null,
      bumpSeq: 0,
    };
  },
  computed: {
    campaignId() {
      const id = parseInt(this.$route.query.campaignId, 10);
      return isNaN(id) ? null : id;
    },
    activeTab() {
      return this.tabs[this.activeIndex] || this.tabs[0];
    },
    mountedTabs() {
      return this.tabs.filter((tab) => this.mountedKeys.indexOf(tab.key) >= 0);
    },
    hasUnsavedChanges() {
      return Object.keys(this.dirtyTabs).some((key) => this.dirtyTabs[key]);
    },
  },
  created() {
    this.$emit("changeLevels", [{ text: "Início" }, { text: "Campanha" }, { text: "Editor (beta)" }]);
    this.activateTab(this.indexFromQuery());
    if (this.campaignId) this.loadHeader();
    window.addEventListener("beforeunload", this.onBeforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges && !window.confirm(LEAVE_CONFIRMATION)) {
      next(false);
      return;
    }
    next();
  },
  watch: {
    // Voltar/avançar do navegador entre abas (?aba=).
    "$route.query.aba"() {
      const index = this.indexFromQuery();
      if (index !== this.activeIndex) this.activateTab(index);
    },
  },
  methods: {
    loadHeader() {
      return service
        .getHeader(this.campaignId)
        .then((header) => {
          this.header = header;
        })
        .catch(() => {});
    },
    indexFromQuery() {
      const index = this.tabs.findIndex((tab) => tab.key === this.$route.query.aba);
      return index >= 0 ? index : 0;
    },
    activateTab(index) {
      this.activeIndex = index;
      const key = this.tabs[index].key;
      if (this.mountedKeys.indexOf(key) < 0) this.mountedKeys.push(key);
      if (this.$route.query.aba !== key) {
        this.$router.replace({ query: Object.assign({}, this.$route.query, { aba: key }) }).catch(() => {});
      }
    },
    onTabChange(value) {
      const index = Number(value);
      if (isNaN(index) || index === this.activeIndex) return;

      // Seção atual com alterações não salvas: confirma antes de sair dela.
      if (this.dirtyTabs[this.activeTab.key] && !window.confirm(
        `A seção "${this.activeTab.label}" tem alterações não salvas. Trocar de seção mesmo assim? (as alterações ficam guardadas até você salvar ou descartar)`
      )) {
        return;
      }
      this.activateTab(index);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    setDirty(key, value) {
      this.$set(this.dirtyTabs, key, value);
    },
    onSaved(event) {
      // Propaga a nova versão para as outras abas que estavam na mesma versão (ver editorTab.mixin).
      this.versionBump = { from: event.from, to: event.to, seq: ++this.bumpSeq };
      if (this.header) {
        this.header = Object.assign({}, this.header, { version: event.to, updatedAt: event.updatedAt });
      }
    },
    onHeaderChanged(changes) {
      if (this.header) this.header = Object.assign({}, this.header, changes);
    },
    onBeforeUnload(event) {
      if (!this.hasUnsavedChanges) return undefined;
      event.preventDefault();
      event.returnValue = LEAVE_CONFIRMATION;
      return LEAVE_CONFIRMATION;
    },
  },
};
</script>

<style scoped>
.ced {
  background: var(--ced-bg);
  margin: -8px -8px 0;
  padding: 16px 24px 40px;
  min-height: 100%;
}
.ced-empty {
  padding: 64px 0;
  text-align: center;
  color: var(--ced-muted);
}
.ced-layout {
  display: grid;
  grid-template-columns: 236px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
  margin-top: 16px;
}
.ced-nav {
  position: sticky;
  top: 80px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px;
  background: var(--ced-surface);
  border: 1px solid var(--ced-border);
  border-radius: var(--ced-radius);
}
.ced-nav__item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--ced-text);
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.ced-nav__item:hover {
  background: var(--ced-bg);
}
.ced-nav__item:focus-visible {
  outline: 2px solid var(--ced-primary);
  outline-offset: 1px;
}
.ced-nav__item--active {
  background: var(--ced-primary-soft);
  color: var(--ced-primary);
  font-weight: 600;
}
.ced-nav__icon {
  width: 18px;
  text-align: center;
  font-size: 15px;
  color: var(--ced-muted);
}
.ced-nav__item--active .ced-nav__icon {
  color: var(--ced-primary);
}
.ced-nav__label {
  flex: 1;
}
.ced-nav__dirty {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ced-warning);
}
.ced-content__head {
  margin-bottom: 16px;
}
.ced-content__title {
  font-size: 22px;
  font-weight: 700;
  color: var(--ced-text);
  margin: 0;
}
.ced-content__description {
  font-size: 14px;
  color: var(--ced-muted);
  margin: 4px 0 0;
}
@media (max-width: 960px) {
  .ced {
    padding: 12px 12px 32px;
  }
  .ced-layout {
    grid-template-columns: minmax(0, 1fr);
  }
  .ced-nav {
    position: static;
    flex-direction: row;
    overflow-x: auto;
    padding: 6px;
  }
  .ced-nav__item {
    width: auto;
    flex: 0 0 auto;
    white-space: nowrap;
  }
}
</style>
